import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Database, Funnel, MagnifyingGlass, X } from '@phosphor-icons/react';
import { DataField, Dataset } from '@/lib/types';
import { DatasetCard } from './DatasetCard';

interface DatasetBrowserProps {
  datasets: Dataset[];
  selectedDatasets: Dataset[];
  onDatasetSelect: (dataset: Dataset) => void;
  onDatasetPreview: (dataset: Dataset) => void;
}

interface DatasetFilterState {
  searchTerm: string;
  dataTypes: string[];
  qualityScore: number;
  minRows: number;
  maxRows: number;
  sortBy: 'name' | 'quality' | 'rows' | 'updated';
  sortOrder: 'asc' | 'desc';
}

export function DatasetBrowser({
  datasets,
  selectedDatasets,
  onDatasetSelect,
  onDatasetPreview,
}: DatasetBrowserProps) {
  const [filters, setFilters] = useState<DatasetFilterState>({
    searchTerm: '',
    dataTypes: [],
    qualityScore: 0,
    minRows: 0,
    maxRows: 0,
    sortBy: 'quality',
    sortOrder: 'desc',
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Extract available data types from all datasets
  const availableDataTypes = useMemo(() => {
    const types = new Set<string>();

    datasets.forEach(dataset => {
      dataset.schema.forEach(field => {
        types.add(field.type);
      });
    });

    return Array.from(types).sort();
  }, [datasets]);

  // Calculate min/max row counts for range filter
  const rowStats = useMemo(() => {
    if (datasets.length === 0) {
      return { min: 0, max: 0 };
    }
    const counts = datasets.map(d => d.totalRows);

    return {
      min: Math.min(...counts),
      max: Math.max(...counts),
    };
  }, [datasets]);

  // Apply filters to datasets
  const filteredDatasets = useMemo(() => {
    let filtered = [...datasets];

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();

      filtered = filtered.filter(
        dataset =>
          dataset.name.toLowerCase().includes(searchLower) ||
          dataset.description.toLowerCase().includes(searchLower) ||
          dataset.source.toLowerCase().includes(searchLower) ||
          dataset.schema.some(
            field =>
              field.name.toLowerCase().includes(searchLower) ||
              field.description?.toLowerCase().includes(searchLower)
          )
      );
    }

    // Data type filter
    if (filters.dataTypes.length > 0) {
      filtered = filtered.filter(dataset =>
        dataset.schema.some(field => filters.dataTypes.includes(field.type))
      );
    }

    // Quality score filter
    if (filters.qualityScore > 0) {
      filtered = filtered.filter(
        dataset => dataset.qualityScore >= filters.qualityScore
      );
    }

    // Row count filters
    if (filters.minRows > 0) {
      filtered = filtered.filter(
        dataset => dataset.totalRows >= filters.minRows
      );
    }
    if (filters.maxRows > 0) {
      filtered = filtered.filter(
        dataset => dataset.totalRows <= filters.maxRows
      );
    }

    // Sort datasets
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'rows':
          aValue = a.totalRows;
          bValue = b.totalRows;
          break;
        case 'updated':
          aValue = new Date(a.lastUpdated);
          bValue = new Date(b.lastUpdated);
          break;
        case 'quality':
        default:
          aValue = a.qualityScore;
          bValue = b.qualityScore;
          break;
      }

      if (typeof aValue === 'string') {
        return filters.sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return filters.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [datasets, filters]);

  // Update filters
  const updateFilters = (newFilters: Partial<DatasetFilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      dataTypes: [],
      qualityScore: 0,
      minRows: 0,
      maxRows: 0,
      sortBy: 'quality',
      sortOrder: 'desc',
    });
  };

  const hasActiveFilters =
    filters.searchTerm ||
    filters.dataTypes.length > 0 ||
    filters.qualityScore > 0 ||
    filters.minRows > 0 ||
    filters.maxRows > 0;

  return (
    <div className='space-y-6'>
      {/* Dataset Filters */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle className='flex items-center gap-2'>
              <Funnel className='w-5 h-5' />
              Dataset Filters
            </CardTitle>
            <div className='flex items-center gap-2'>
              <Badge variant='secondary'>
                {filteredDatasets.length} of {datasets.length} datasets
              </Badge>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className='space-y-4'>
          {/* Search Bar - Always visible */}
          <div className='relative'>
            <MagnifyingGlass className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
            <Input
              placeholder='Search datasets, fields, or descriptions...'
              value={filters.searchTerm}
              onChange={e => updateFilters({ searchTerm: e.target.value })}
              className='pl-10'
            />
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className='flex flex-wrap gap-2 items-center'>
              <span className='text-sm text-muted-foreground'>
                Active filters:
              </span>
              {filters.dataTypes.map(type => (
                <Badge key={type} variant='outline' className='gap-1'>
                  {type}
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() =>
                      updateFilters({
                        dataTypes: filters.dataTypes.filter(t => t !== type),
                      })
                    }
                  />
                </Badge>
              ))}
              {filters.qualityScore > 0 && (
                <Badge variant='secondary' className='gap-1'>
                  Quality ≥{filters.qualityScore}%
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() => updateFilters({ qualityScore: 0 })}
                  />
                </Badge>
              )}
              {filters.minRows > 0 && (
                <Badge variant='secondary' className='gap-1'>
                  Rows ≥{filters.minRows.toLocaleString()}
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() => updateFilters({ minRows: 0 })}
                  />
                </Badge>
              )}
              {filters.maxRows > 0 && (
                <Badge variant='secondary' className='gap-1'>
                  Rows ≤{filters.maxRows.toLocaleString()}
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() => updateFilters({ maxRows: 0 })}
                  />
                </Badge>
              )}
              <Button variant='ghost' size='sm' onClick={clearFilters}>
                Clear All
              </Button>
            </div>
          )}

          {/* Expanded Filters */}
          {isExpanded && (
            <>
              <Separator />
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
                {/* Data Types */}
                <div className='space-y-3'>
                  <Label className='text-sm font-semibold'>Data Types</Label>
                  <div className='space-y-2'>
                    {availableDataTypes.map(type => {
                      const count = datasets.filter(dataset =>
                        dataset.schema.some(field => field.type === type)
                      ).length;

                      return (
                        <div key={type} className='flex items-center space-x-2'>
                          <Checkbox
                            id={`type-${type}`}
                            checked={filters.dataTypes.includes(type)}
                            onCheckedChange={checked => {
                              if (checked) {
                                updateFilters({
                                  dataTypes: [...filters.dataTypes, type],
                                });
                              } else {
                                updateFilters({
                                  dataTypes: filters.dataTypes.filter(
                                    t => t !== type
                                  ),
                                });
                              }
                            }}
                          />
                          <label
                            htmlFor={`type-${type}`}
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            {type} ({count})
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quality Filter */}
                <div className='space-y-3'>
                  <Label className='text-sm font-semibold'>Quality Score</Label>
                  <Select
                    value={filters.qualityScore.toString()}
                    onValueChange={value =>
                      updateFilters({ qualityScore: parseInt(value) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Any quality' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='0'>Any quality</SelectItem>
                      <SelectItem value='70'>70% or higher</SelectItem>
                      <SelectItem value='80'>80% or higher</SelectItem>
                      <SelectItem value='90'>90% or higher</SelectItem>
                      <SelectItem value='95'>95% or higher</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Row Count Filters */}
                <div className='space-y-3'>
                  <Label className='text-sm font-semibold'>Row Count</Label>
                  <div className='space-y-2'>
                    <div>
                      <Label
                        htmlFor='min-rows'
                        className='text-xs text-muted-foreground'
                      >
                        Min rows
                      </Label>
                      <Input
                        id='min-rows'
                        type='number'
                        placeholder={`Min (${rowStats.min.toLocaleString()})`}
                        value={filters.minRows || ''}
                        onChange={e =>
                          updateFilters({
                            minRows: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor='max-rows'
                        className='text-xs text-muted-foreground'
                      >
                        Max rows
                      </Label>
                      <Input
                        id='max-rows'
                        type='number'
                        placeholder={`Max (${rowStats.max.toLocaleString()})`}
                        value={filters.maxRows || ''}
                        onChange={e =>
                          updateFilters({
                            maxRows: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Sort Options */}
                <div className='space-y-3'>
                  <Label className='text-sm font-semibold'>Sort Options</Label>
                  <div className='space-y-2'>
                    <Select
                      value={filters.sortBy}
                      onValueChange={(value: DatasetFilterState['sortBy']) =>
                        updateFilters({ sortBy: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='quality'>Quality Score</SelectItem>
                        <SelectItem value='name'>Name</SelectItem>
                        <SelectItem value='rows'>Row Count</SelectItem>
                        <SelectItem value='updated'>Last Updated</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.sortOrder}
                      onValueChange={(value: DatasetFilterState['sortOrder']) =>
                        updateFilters({ sortOrder: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='desc'>Descending</SelectItem>
                        <SelectItem value='asc'>Ascending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Filtered Datasets Grid */}
      {filteredDatasets.length > 0 ? (
        <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'>
          {filteredDatasets.map(dataset => (
            <DatasetCard
              key={dataset.id}
              dataset={dataset}
              onSelect={onDatasetSelect}
              onPreview={onDatasetPreview}
              isSelected={selectedDatasets.some(d => d.id === dataset.id)}
            />
          ))}
        </div>
      ) : (
        <div className='text-center py-12'>
          <Database className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
          <h3 className='text-xl font-semibold mb-2'>No Datasets Found</h3>
          <p className='text-muted-foreground mb-4'>
            Try adjusting your filters to see more datasets
          </p>
          <Button onClick={clearFilters} variant='outline'>
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
